'use client';

import { PropsWithChildren, ReactNode, useRef } from 'react';
import styles from './breadthofcontentaccessibilitylist.module.scss';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Button } from '@/ui-components/atoms/Button/Button';

export interface BreadthOfContentAccessibilityListProps extends PropsWithChildren {
    items: ReactNode[];
}

export function BreadthOfContentAccessibilityList({
    items = [],
    children,
}: BreadthOfContentAccessibilityListProps): React.JSX.Element {
    const goToBeginningOfLinksButtonRef = useRef<HTMLDivElement>(null);
    const skipLinksButtonRef = useRef<HTMLButtonElement>(null);

    return (
        <div className={styles.wrapper}>
            <div
                aria-hidden="true"
                tabIndex={-1}
                onKeyUp={(event) => handleSliderKeyEvent(event, skipLinksButtonRef.current)}
                onKeyDown={(event) => handleSliderKeyEvent(event, skipLinksButtonRef.current)}
                className={styles.contentContainer}
            >
                {children}
            </div>

            {!!items.length && (
                <div className={styles.accessibilityItemSetContainer}>
                    <Grid container>
                        <Grid col={{ md: 8, lg: 6 }} start={{ md: 3, lg: 4 }}>
                            <>
                                <div className={styles.skipButtonContainer}>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        fitting="hug-contents"
                                        size="small"
                                        onClick={() => goToBeginningOfLinksButtonRef.current?.focus()}
                                        ref={skipLinksButtonRef}
                                    >
                                        Skip list
                                    </Button>
                                </div>

                                <div className={styles.listItemsContainer}>{items}</div>

                                <div className={styles.skipButtonContainer}>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        fitting="hug-contents"
                                        onClick={() => skipLinksButtonRef.current?.focus()}
                                        ref={goToBeginningOfLinksButtonRef}
                                    >
                                        Go to beginning of list
                                    </Button>
                                </div>
                            </>
                        </Grid>
                    </Grid>
                </div>
            )}
        </div>
    );
}

function handleSliderKeyEvent(event: React.KeyboardEvent, itemToFocus: HTMLElement | null) {
    const keysToIgnore = ['Alt', 'Control', 'Meta', 'Shift'];

    if (!itemToFocus || keysToIgnore.includes(event.key)) {
        return;
    }

    const target = event.currentTarget;
    const elementsToHideFromAriaTech = [...target.querySelectorAll('a, li')];
    // If you need a true Array for methods like .map(), .filter(), etc.

    elementsToHideFromAriaTech.forEach((element) => {
        // Stopping inner elements from taking focus when navigating backwards out of the accessibility list:
        element.setAttribute('tabindex', '-1');
        element.setAttribute('aria-hidden', 'true');
    });

    itemToFocus?.focus();
}
